import { ExtendCustomEventMap } from "@pathwright/utils/src/event/typing"
import defaults from "lodash/defaults"
import {
  MutableRefObject,
  useCallback,
  useEffect,
  useRef,
  useState
} from "react"
import { usePrevious } from "react-use"

type LocalStorageChangeEventDetail = {
  key: string
  newValue: any
}

type LocalStorageChangeEvent = CustomEvent<LocalStorageChangeEventDetail>

// Extending the CustomEventMap with our own localstorage:change event key.
declare global {
  interface Window
    extends ExtendCustomEventMap<{
      ["localstorage:change"]: LocalStorageChangeEvent
    }> {}
}

// `storageType` - Whether to store changes in localStorage or sessionStorage.
// `sync`        - Whether to sync the local changes "globally" (to other browser tabs).
//                 Only possible when using the "local" `storageType` (via localStorage).
// `migrateKey`  - If set, then when the key changes, the value of the migrateKey will be copied to the new key.
//                 The migrateKey will be removed after the value has been copied to the new key.
export type LocalStorageOptions = {
  migrateKey?: string
} & (
  | {
      storageType?: "local"
      sync?: boolean
    }
  | {
      storageType?: "session"
      sync?: false
    }
)

// "local": The change occurred locally, in the current web page.
// "global": The change occurred globally, from a different web page.
export type LocalStorageSource = "local" | "global"

// Data detailing info about the stored value.
//   - source: The actor responsible for the current value, one of LocalStorageSource
//   - valueRef: A ref to the current stored value.
export type LocalStorageMeta<T extends any> = {
  source: LocalStorageSource
  valueRef: MutableRefObject<T | undefined>
}

export type LocalStorageValue<T extends any> =
  | T
  | ((value?: T) => T | undefined)

export type LocalStorageSetter<T extends any> = (
  value?: LocalStorageValue<T>
) => void

export type LocalStorageReturn<T extends any> = [
  T | undefined,
  LocalStorageSetter<T>,
  LocalStorageMeta<T>
]

function useLocalStorage<T extends any>(
  key: string,
  initialValue?: LocalStorageValue<T>,
  options: LocalStorageOptions = {}
): LocalStorageReturn<T> {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const { storageType, sync, migrateKey } = defaults(options, {
    storageType: "local",
    sync: false
  })

  const sourceRef = useRef<LocalStorageSource>("local")

  const storage =
    window[`${storageType}Storage` as "localStorage" | "sessionStorage"]

  if (!storage) {
    throw Error(
      `Cannot find storage on window for storage type: "${storageType}"`
    )
  }

  const getInitialValue = () => {
    const handleInitialValue = (result?: T) => {
      if (initialValue instanceof Function) {
        return initialValue(result)
      }
      // Ensure we return result even if it's falsey, unless it's null which
      // indicates the key doesn't exist.
      return result !== null ? result : initialValue
    }

    if (!key) {
      return handleInitialValue()
    }

    if (migrateKey) {
      const migrateValue = storage.getItem(migrateKey)
      if (migrateValue) storage.setItem(key, migrateValue)
      storage.removeItem(migrateKey)
    }

    try {
      // Get from local storage by key
      const item = storage.getItem(key)
      // Parse stored json or if none return initialValue
      return handleInitialValue(item && JSON.parse(item))
    } catch (error) {
      // If error also return initialValue
      console.log(error)
      return handleInitialValue()
    }
  }

  const [storedValue, setStoredValue] = useState<T | undefined>(getInitialValue)

  // Wrapping setStoredValue to also update storedValueRef.
  function setStateAndRefValue(value?: T) {
    storedValueRef.current = value
    setStoredValue(value)
  }

  // For tracking current storedValue.
  const storedValueRef = useRef<T | undefined>(storedValue)

  // If the key changes, set the initial value for that key.
  const previousKey = usePrevious(key)
  useEffect(() => {
    if (previousKey && key !== previousKey) setStoredValue(getInitialValue())
  }, [key])

  const setValueToStore = useCallback(
    (valueToStore?: T) => {
      try {
        // Calling setValue with no argument provides simple mechanic for
        // removing item from localStorage.
        if (typeof valueToStore === "undefined") {
          storage.removeItem(key)
        } else {
          // Save to local storage
          storage.setItem(key, JSON.stringify(valueToStore))
        }
        // Save state
        setStateAndRefValue(valueToStore)

        // Sync the change to other listeners in the same web page.
        // NOTE: wrapping with setTimeout to ensure this event is dispatched
        // after the current call stack is complete. There is possibly a window of
        // time between this event firing and a consuming hook adding its listener,
        // where the consuming hook has stale data and misses the event. So, beware
        // if this behaviour causes issues or needs changing.
        if (sourceRef.current === "local") {
          setTimeout(() => {
            window.dispatchEvent(
              new CustomEvent<LocalStorageChangeEventDetail>(
                "localstorage:change",
                {
                  detail: {
                    key,
                    newValue: valueToStore
                  }
                }
              )
            )
          }, 0)
        }
      } catch (error) {
        // A more advanced implementation would handle the error case
        console.log(error)
      }
    },
    [key, storage]
  )

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue: LocalStorageSetter<T> = useCallback(
    (value) => {
      sourceRef.current = "local"
      // Allow value to be a function so we have same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValueRef.current) : value

      setValueToStore(valueToStore)
    },
    [setValueToStore]
  )

  // Respond to "local" local storage change.
  useEffect(() => {
    function handler({ detail }: CustomEvent<LocalStorageChangeEventDetail>) {
      if (detail.key === key && detail.newValue !== storedValueRef.current) {
        setStateAndRefValue(detail.newValue)
      }
    }

    // Struggling with the typing...
    const h: EventListener = async (e) => handler(e as LocalStorageChangeEvent)

    window.addEventListener("localstorage:change", h)
    return () => window.removeEventListener("localstorage:change", h)
  }, [key])

  // Sync changes in localStorage from other same-domain web pages.
  // Keep in mind that this does not sync changes in localStorage for
  // the same web page making the change.
  useEffect(() => {
    if (sync) {
      const handler = (e: StorageEvent) => {
        if (e.key === key) {
          sourceRef.current = "global"
          // e.newValue will be null when the item has been removed
          // from localStorage.
          if (e.newValue === null) {
            setValueToStore()
          } else {
            setValueToStore(JSON.parse(e.newValue))
          }
        }
      }
      // Add and remove listener.
      window.addEventListener("storage", handler)
      return () => window.removeEventListener("storage", handler)
    }
  }, [options?.sync])

  const meta = { source: sourceRef.current, valueRef: storedValueRef }

  return [storedValue, setValue, meta]
}

export const useSessionStorage: typeof useLocalStorage = (key, initialValue) =>
  useLocalStorage(key, initialValue, { storageType: "session" })

export default useLocalStorage
